import React, { useId, useState } from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import { Switch } from "@coval/ui"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import { TierGauge } from "@/components/charts/TierGauge"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { PlayheadTimeSeriesLineChart } from "../components/PlayheadTimeSeriesLineChart"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"
import { formatTimeToMinSec } from "../mixins/WithLLMAudioRegionsMixin"
import {
  contourTurns,
  directionLabel,
  expectedTone,
  FLAT_BAND_ST,
  GAUGE_MAX_PCT,
  MISMATCH_FILL,
  mismatchBreakdown,
  NATURAL_MAX_PCT,
  sentenceLabel,
  terminalTier,
} from "./terminalContourValues"

const log = logger("TerminalContourMetric")

const MISMATCH_REGION_ALPHA = 0.25
// Expected-tone arrows: grey and slightly transparent so they read as a guide.
const EXPECTED_TONE_COLOR = "rgba(113, 113, 122, 0.7)"

interface TimelineSeriesPoint {
  t: number
  value: number | null
  direction: string
  sentenceType: string
  // Index signature required to satisfy the chart's TimeSeriesPoint shape; extra
  // fields (direction, sentenceType) flow through to the tooltip.
  [key: string]: number | string | null | undefined
}

interface ExpectedToneMarker {
  x: number
  y: number
  glyph: string
  color: string
}

// Per-sentence terminal-Δ timeline with a toggle for the expected-tone arrows.
function TerminalContourTimeline({
  series,
  maxX,
  expectedMarkers,
  highlights,
}: {
  series: TimelineSeriesPoint[]
  maxX: number
  expectedMarkers: ExpectedToneMarker[]
  highlights: { start: number; end: number; fill: string }[]
}): React.ReactElement {
  const [showExpected, setShowExpected] = useState(true)
  const switchId = useId()
  const hasExpected = expectedMarkers.length > 0
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] text-coval-text-muted">
          {showExpected && hasExpected
            ? "red = mismatch · ↑↓ = expected tone · click to seek"
            : "red = mismatch · click to seek"}
        </div>
        {hasExpected && (
          <div className="flex items-center gap-1.5">
            <label
              htmlFor={switchId}
              className="text-[10px] text-coval-text-muted cursor-pointer"
            >
              Expected tone
            </label>
            <Switch
              id={switchId}
              aria-label="Show expected-tone arrows"
              checked={showExpected}
              onCheckedChange={setShowExpected}
            />
          </div>
        )}
      </div>
      <PlayheadTimeSeriesLineChart
        data={series}
        maxX={maxX}
        yAxisLabel="terminal Δ (st)"
        formatX={formatTimeToMinSec}
        formatY={(v) => `${v > 0 ? "+" : ""}${Math.round(v)}`}
        referenceLines={[-FLAT_BAND_ST, 0, FLAT_BAND_ST]}
        markers={showExpected ? expectedMarkers : []}
        highlights={highlights}
        formatTooltipValue={(p) => {
          const delta =
            typeof p.value === "number"
              ? `${p.value >= 0 ? "+" : ""}${p.value.toFixed(1)} st`
              : "—"
          const dir =
            typeof p.direction === "string" ? directionLabel(p.direction) : ""
          const sentence =
            typeof p.sentenceType === "string"
              ? sentenceLabel(p.sentenceType)
              : ""
          return dir && sentence ? `${delta} · ${dir} on ${sentence}` : delta
        }}
        tooltipLabel="Terminal pitch"
        label="Terminal pitch change per sentence"
        summary={`${series.length} sentence${series.length === 1 ? "" : "s"}; ${highlights.length} shaded as a mismatch with the expected tone.`}
        xLabel="Sentence start"
        stopClickPropagation
        onPointClick={(t) =>
          simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, { time: t })
        }
      />
    </div>
  )
}

export class TerminalContourMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "%"
  aggregate_units = "%"

  private renderSummary(mismatchPct: number): React.ReactNode {
    return (
      <TierGauge
        value={mismatchPct}
        tier={terminalTier(mismatchPct)}
        min={0}
        max={GAUGE_MAX_PCT}
        band={{ low: 0, high: NATURAL_MAX_PCT }}
        formatValue={(v) => v.toFixed(0)}
        unit="%"
        maxTickLabel={`${GAUGE_MAX_PCT}+%`}
        bandNote={`Shaded = natural range (≤${NATURAL_MAX_PCT}% of sentences mismatched).`}
      />
    )
  }

  // Positive Δ = rising tone, negative = falling; the ±flat band marks level.
  // Mismatched sentences are shaded red.
  private renderTimeline(mo: RunMetricOutput): React.ReactNode | null {
    const turns = contourTurns(mo.subvalues_by_timestamp ?? [])
    if (turns.length < 2) {
      return null
    }
    const series = turns.map((turn) => ({
      t: turn.start,
      value: turn.deltaSt,
      direction: turn.direction,
      sentenceType: turn.sentenceType,
    }))
    // Arrow on each flagged sentence showing the tone it should have had (↑ rise for a
    // yes/no question, ↓ fall for a statement).
    const expectedMarkers = turns
      .filter((turn) => turn.isMismatch)
      .flatMap((turn) => {
        const expected = expectedTone(turn.sentenceType)
        return expected
          ? [
              {
                x: turn.start,
                y: expected.targetSt,
                glyph: expected.glyph,
                color: EXPECTED_TONE_COLOR,
              },
            ]
          : []
      })
    const highlights = turns
      .filter((turn) => turn.isMismatch)
      .map((turn) => ({
        start: turn.start,
        end: turn.end,
        fill: MISMATCH_FILL,
      }))
    return (
      <TerminalContourTimeline
        series={series}
        maxX={turns[turns.length - 1].end}
        expectedMarkers={expectedMarkers}
        highlights={highlights}
      />
    )
  }

  private renderBreakdown(mo: RunMetricOutput): React.ReactNode | null {
    const { classified, mismatched, uptalk, flatQuestion } = mismatchBreakdown(
      mo.subvalues_by_timestamp ?? [],
    )
    if (classified === 0) {
      return null
    }
    return (
      <p className="text-xs text-coval-text-muted">
        {mismatched} of {classified} classifiable sentence
        {classified === 1 ? "" : "s"} mismatched — {uptalk} uptalk,{" "}
        {flatQuestion} flat yes/no question
        {flatQuestion === 1 ? "" : "s"}. Wh-questions and falling questions are
        left unflagged.
      </p>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const mismatchPct = mo.float_value
      if (mismatchPct == null) {
        return (
          <div className="text-center py-8 text-coval-text-muted">
            No terminal contour data available
          </div>
        )
      }
      return (
        <div className="space-y-2">
          {this.renderSummary(mismatchPct)}
          {this.renderTimeline(mo)}
          {this.renderBreakdown(mo)}
        </div>
      )
    } catch (error) {
      log.error(
        "Error rendering terminal contour deep dive",
        error instanceof Error ? error : undefined,
      )
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  // Expanded MetricGrid layout: summary slider beside the comments column, the
  // per-turn timeline and breakdown span the panel width below.
  getDeepDiveSections(
    mo: RunMetricOutput,
  ): { label: string; content: React.ReactNode }[] | null {
    try {
      const mismatchPct = mo.float_value
      if (mismatchPct == null) {
        return null
      }
      return [
        {
          label: "Sentence intonation",
          content: (
            <div className="space-y-4">
              {this.renderTimeline(mo)}
              {this.renderBreakdown(mo)}
            </div>
          ),
        },
        // No label: the metric card header already reads "Sentence Intonation".
        { label: "", content: this.renderSummary(mismatchPct) },
      ]
    } catch {
      return null
    }
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      return contourTurns(mo.subvalues_by_timestamp ?? [])
        .filter((t) => t.isMismatch)
        .map((t, idx) => ({
          id: `terminal_mismatch:${idx}`,
          start: t.start,
          end: t.end,
          color: `rgba(${MISMATCH_FILL}, ${MISMATCH_REGION_ALPHA})`,
          drag: false,
          resize: false,
        }))
    } catch (e) {
      log.debug("Terminal contour regions unavailable or could not parse", {
        error: e,
      })
      return []
    }
  }
}
